import {
    ChangeUserRoleParams,
    CreateEmployerUserParams,
    EmployerUser,
    EmployerUserState,
    FetchState
} from "@/shared/types";
import {
    getUserAccountsForEmployer,
    resendUserActivationLink,
    changeUserRole,
    addEmployerUser,
    updateEmployerUser,
    deleteEmployerUser
} from "@/shared/api/userService";
import Vue from "vue";
import _ from "lodash";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import { useUserStore } from "@/shared/store/user";

export const initialState: EmployerUserState = {
    employerUsers: [],
    postFetchState: FetchState.Uninitialized
};

export interface EmployeesListParams {
    employerId: number;
    pageNumber: number;
    searchTerm: string;
    clearState: boolean;
}

export const useEmployerUserStore = defineStore("employerUser", {
    state: (): EmployerUserState => (_.cloneDeep(initialState)),
    getters: {
        users: state => state.employerUsers,
        postingUpdate: state => state.postFetchState === FetchState.Loading,
    },
    actions: {
        setUsers (employeeUsers: EmployerUser[]) {
            this.employerUsers = employeeUsers;
        },
        invitationSent (userId) {
            const user = this.employerUsers.filter(e => e.userId === userId);
            Vue.set(user[0], "invitationSent", true);
        },
        setUser (user: EmployerUser) {
            const employerUserId = user.employerUserId;
            let index = this.employerUsers.findIndex(e => e.employerUserId === employerUserId);
            index = (index < 0) ? this.employerUsers.length : index;
            Vue.set(this.employerUsers, index, user);
        },
        removeUser (userId: number) {
            const filteredEmployerUsers = _.filter(this.employerUsers, x => {
                return x.userId !== userId;
            });
            this.employerUsers = [ ...filteredEmployerUsers ];
        },
        setPostFetchState (fetchState: FetchState) {
            this.postFetchState = fetchState;
        },
        getEmployerUsers (employerId?: number) {
            if (!employerId) {
                const userStore = useUserStore();
                employerId = userStore.employerId;
            }

            return getUserAccountsForEmployer(employerId).then(resp => {
                this.setUsers(resp.data);
            });
        },
        resendEmployerUserSignupLink (arg: { userId: number, employerId: number }) {
            this.setPostFetchState(FetchState.Loading);

            return resendUserActivationLink(arg.userId, arg.employerId).then(resp => {
                    this.invitationSent(arg.userId);
                })
                .finally(() => this.setPostFetchState(FetchState.Loaded));
        },
        deleteEmployerUser (arg: { userId: number, employerId: number }) {
            if (this.postFetchState === FetchState.Loading) {
                return;
            }

            this.setPostFetchState(FetchState.Loading);
            return deleteEmployerUser(arg.userId, arg.employerId)
                .then(resp => {
                    this.removeUser(arg.userId);
                })
                .catch(error => storeHelper.handleError({ error, rethrow: false }))
                .finally(() => this.setPostFetchState(FetchState.Loaded));
        },
        changeEmployerUserRoles (changeUserRolerParams: ChangeUserRoleParams) {
            return changeUserRole(changeUserRolerParams)
                .then(resp => {
                    this.setUser(resp.data);
                })
                .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        addUser (createEmployerUserParams: CreateEmployerUserParams): Promise<{
            emailError: boolean
        }> {
            return addEmployerUser(createEmployerUserParams)
                .then(resp => {
                    this.setUser(resp.data);
                    return {
                        emailError: resp.data.inviteEmailFailed === true,
                        serviceUnavailable: resp.data.statusCode === 503,
                        serviceErrorMessage: resp.data.serviceErrorMessage,
                        reactivatingUser: resp.data.reactivatingExistingEmployerUser === true
                    };
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        updateEmployerUser (employerUser: EmployerUser): Promise<{
            updateError: boolean
        }> {
            this.setPostFetchState(FetchState.Loading);
            const userStore = useUserStore();
            return updateEmployerUser(userStore.employerId, employerUser)
                .then(resp => {
                    this.setUser(resp.data);
                    return { updateError: false };
                })
                .catch(error => storeHelper.handleError({ error, rethrow: false }))
                .finally(() => this.setPostFetchState(FetchState.Loaded));
        }
    }
});
